import { useEffect, useRef, useState, useCallback, type RefObject } from 'react';
import { clippingAncestorsWithin, fixedContainingBlock } from './stickyUtils';

interface StickyScrollbarProps {
  scrollRef: RefObject<HTMLDivElement | null>;
  isFullscreen: boolean;
}

/**
 * A fixed-position horizontal scrollbar that syncs with the table's scroll container.
 * Pinned to the bottom of the viewport (or fullscreen container) so users can
 * scroll horizontally even when the table extends below the viewport.
 *
 * Always renders the DOM node (hidden via display:none) to keep refs stable
 * for scroll sync listeners.
 */
const SCROLLBAR_HEIGHT = 14;

export function StickyScrollbar({ scrollRef, isFullscreen }: StickyScrollbarProps) {
  const barRef = useRef<HTMLDivElement>(null);
  const spacerRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  // `bottom` is the scroll container's bottom edge in viewport coords —
  // used to attach the scrollbar directly below a short table instead of
  // floating at the viewport bottom.
  // `viewportBottom` is the viewport's bottom edge in the same coordinates.
  const [rect, setRect] = useState({ left: 0, width: 0, bottom: 0, viewportBottom: 0 });
  const syncing = useRef(false);

  // The bar's frame of reference, looked up once and refreshed only on
  // resize / an ancestor's animation (the ancestor walk calls
  // getComputedStyle, too costly for every scroll): `block` is the
  // ancestor that establishes the containing block of `position: fixed`
  // (null = the viewport — a page table); inside one (a centred dialog),
  // `clips` are the ancestors that clip the table (its frame, the dialog
  // body); the highest of their bottoms stands in for the viewport bottom so a tall table
  // pins the bar inside the dialog, as a page table pins it to the window.
  const frameRef = useRef<{ block: HTMLElement | null; clips: HTMLElement[] } | null>(null);
  const resolveFrame = useCallback(() => {
    const el = scrollRef.current;
    const block = fixedContainingBlock(barRef.current);
    frameRef.current = { block, clips: block && el ? clippingAncestorsWithin(el, block) : [] };
  }, [scrollRef]);

  // Track scroll container dimensions and overflow state
  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const overflow = el.scrollWidth > el.clientWidth + 1;
    setHasOverflow(overflow);

    if (spacerRef.current) {
      spacerRef.current.style.width = `${el.scrollWidth}px`;
    }

    if (!frameRef.current) resolveFrame();
    const { block, clips } = frameRef.current ?? { block: null, clips: [] };
    const bounds = el.getBoundingClientRect();
    // A fixed element is placed against the containing block's PADDING box: step
    // inside its border (`clientLeft` / `clientTop`), e.g. a dialog's 1 px border.
    const blockBox = block?.getBoundingClientRect();
    const origin = blockBox && block
      ? { left: blockBox.left + block.clientLeft, top: blockBox.top + block.clientTop }
      : { left: 0, top: 0 };
    const visibleBottom = clips.reduce((min, c) => Math.min(min, c.getBoundingClientRect().bottom), window.innerHeight);
    const next = {
      left: bounds.left - origin.left,
      width: bounds.width,
      bottom: bounds.bottom - origin.top,
      viewportBottom: visibleBottom - origin.top,
    };
    setRect((prev) =>
      prev.left === next.left &&
      prev.width === next.width &&
      prev.bottom === next.bottom &&
      prev.viewportBottom === next.viewportBottom
        ? prev
        : next,
    );
  }, [scrollRef, resolveFrame]);

  // ResizeObserver to track table size changes
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    measure();

    const ro = new ResizeObserver(() => requestAnimationFrame(measure));
    ro.observe(el);

    // Also observe the table element inside for column changes
    const table = el.querySelector('table');
    if (table) ro.observe(table);

    return () => ro.disconnect();
  }, [scrollRef, measure]);

  // IntersectionObserver to hide when table is not in viewport
  useEffect(() => {
    if (isFullscreen) {
      setIsVisible(true);
      return;
    }

    const el = scrollRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0 }
    );
    io.observe(el);

    return () => io.disconnect();
  }, [scrollRef, isFullscreen]);

  // Sync scroll positions bidirectionally
  useEffect(() => {
    const el = scrollRef.current;
    const bar = barRef.current;
    if (!el || !bar) return;

    const onTableScroll = () => {
      if (syncing.current) return;
      syncing.current = true;
      bar.scrollLeft = el.scrollLeft;
      requestAnimationFrame(() => { syncing.current = false; });
    };

    const onBarScroll = () => {
      if (syncing.current) return;
      syncing.current = true;
      el.scrollLeft = bar.scrollLeft;
      requestAnimationFrame(() => { syncing.current = false; });
    };

    el.addEventListener('scroll', onTableScroll, { passive: true });
    bar.addEventListener('scroll', onBarScroll, { passive: true });

    return () => {
      el.removeEventListener('scroll', onTableScroll);
      bar.removeEventListener('scroll', onBarScroll);
    };
  }, [scrollRef, isFullscreen]);

  // Re-measure on window resize + any page/ancestor scroll so the bar
  // follows a short table's bottom edge. rAF-throttled to keep scroll
  // handlers cheap.
  useEffect(() => {
    let raf = 0;
    let reframe = false;
    const schedule = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        if (reframe) {
          reframe = false;
          resolveFrame();
        }
        measure();
      });
    };
    const scheduleReframe = () => {
      reframe = true;
      schedule();
    };
    // A table inside an animating container (a dialog zooming in) is first
    // measured mid-animation, scaled and offset; re-measure when an
    // ANCESTOR's animation or transition ends so the bar lands on the
    // settled table. Unrelated animations (a hovered button fading) are
    // ignored.
    const onAnimationDone = (e: Event) => {
      const el = scrollRef.current;
      const target = e.target as Node | null;
      if (el && target && typeof target.contains === 'function' && target.contains(el)) scheduleReframe();
    };
    window.addEventListener('resize', scheduleReframe, { passive: true });
    // Capture phase catches scrolls in ancestors (app layout, page, etc.)
    // where scroll events don't bubble to window on their own.
    document.addEventListener('scroll', schedule, { passive: true, capture: true });
    document.addEventListener('animationend', onAnimationDone, { passive: true, capture: true });
    document.addEventListener('transitionend', onAnimationDone, { passive: true, capture: true });
    return () => {
      window.removeEventListener('resize', scheduleReframe);
      document.removeEventListener('scroll', schedule, true);
      document.removeEventListener('animationend', onAnimationDone, true);
      document.removeEventListener('transitionend', onAnimationDone, true);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [measure, resolveFrame, scrollRef]);

  // In non-fullscreen, sit the bar directly below a short table
  // (top = tableBottom) or pinned to the viewport bottom when the table
  // extends past the viewport. Formula `min(tableBottom, viewportBottom
  // - height)` places the bar's top edge below the last row in the
  // short-table case and at (viewportBottom - height) for tall tables.
  const viewportBottom = rect.viewportBottom || (typeof window !== 'undefined' ? window.innerHeight : rect.bottom);
  const topPx = Math.min(rect.bottom, viewportBottom - SCROLLBAR_HEIGHT);
  // "Attached" = the bar sits right at tableBottom. In that mode we keep
  // the strip visible even without overflow so the rounded-corner frame
  // feels like a natural footer of the table. In tall-table mode the bar
  // pins to the viewport edge — hide there when there's no scrollable
  // content to avoid an empty floating strip.
  const attachedToTable = !isFullscreen && topPx === rect.bottom;
  const hidden = !isVisible || (!attachedToTable && !hasOverflow);

  return (
    <div
      ref={barRef}
      role="presentation"
      aria-hidden="true"
      className={`editable-table-fixed-scrollbar sticky-scrollbar ${isFullscreen ? 'editable-table-fixed-scrollbar--fullscreen' : ''}`}
      style={{
        ...(isFullscreen
          ? undefined
          : {
              // Span the host wrapper's inner content width — the host
              // now reserves a 14px pb for the scrollbar, so the bar
              // sits inside the host's rounded frame rather than below
              // it. No side borders needed (the host provides them).
              left: rect.left,
              width: rect.width,
              top: topPx,
              bottom: 'auto',
            }),
        // In tall-table mode the bar pins to the viewport bottom and
        // floats free of the host wrapper; round its bottom corners so
        // it doesn't look like a blunt strip.
        ...(!attachedToTable && !isFullscreen && {
          borderBottomLeftRadius: 'var(--radius)',
          borderBottomRightRadius: 'var(--radius)',
        }),
        display: hidden ? 'none' : undefined,
      }}
    >
      <div ref={spacerRef} style={{ height: 1 }} />
    </div>
  );
}
